import {
  GatewayControleQuinquennal,
  GatewayEntreprise,
} from "@conformite/gateway";
import {
  ComplianceProgressBar,
  IconStatus,
  Link,
  TagWithIcon,
  Button,
  Card,
  displayErrorToast,
} from "@endya-conformite/ui-kit";
import ArrowRight from "@endya-conformite/ui-kit/src/assets/icons/arrow-right.svg?react";
import DownloadIcon from "@endya-conformite/ui-kit/src/assets/icons/download.svg?react";
import { ControleQuinquennalApi } from "@src/api/controleQuinquennal.api";
import { handleGatewayError } from "@src/api/utils/handleGatewayError";
import { downloadFile } from "@src/helper/file.helper";
import { AxiosError } from "axios";
import classNames from "classnames";
import { format } from "date-fns";
import styles from "./ControleQuinquennalCard.module.scss";

type ControleQuinquennalCardActionProps = {
  controleQuinquennal: GatewayControleQuinquennal.ControleQuinquennalWithCompletion;
};

function ControleQuinquennalCardAction({
  controleQuinquennal,
}: ControleQuinquennalCardActionProps) {
  switch (controleQuinquennal.status) {
    case GatewayControleQuinquennal.ControleQuinquennalStatus.EN_COURS:
    case GatewayControleQuinquennal.ControleQuinquennalStatus.DEPOSE:
      return (
        <Link to={`/controle-quinquennal/${controleQuinquennal.id}`}>
          <Button
            variant="text"
            className={styles.action}
            icon={<ArrowRight />}
          >
            Accéder
          </Button>
        </Link>
      );
    case GatewayControleQuinquennal.ControleQuinquennalStatus.TERMINE:
      return (
        <Button
          variant="text"
          icon={<DownloadIcon />}
          onClick={() => {
            ControleQuinquennalApi.getRapport(controleQuinquennal.id)
              .then(async (rapport) => {
                if (!rapport) return;
                const file = await ControleQuinquennalApi.downloadRapport(
                  controleQuinquennal.id
                );
                downloadFile(file, rapport.filename);
              })
              .catch(
                handleGatewayError({
                  onUnhandled: (error) => {
                    const axiosError = error as AxiosError;
                    if (axiosError.status === 404) {
                      displayErrorToast(
                        "Aucun rapport n'a été trouvé pour ce contrôle quinquennal."
                      );
                    } else {
                      displayErrorToast(
                        "Une erreur est survenue lors du téléchargement du rapport."
                      );
                    }
                    console.error(error);
                  },
                })
              );
          }}
        >
          Télécharger le rapport
        </Button>
      );
    case GatewayControleQuinquennal.ControleQuinquennalStatus.ANNULE:
      return null;
    default:
      throw new Error(`Unknown status ${controleQuinquennal.status as string}`);
  }
}

type ControleQuinquennalCardProps = {
  controleQuinquennal: GatewayControleQuinquennal.ControleQuinquennalWithCompletion;
  entreprise?: GatewayEntreprise.EntrepriseAControllerResponseType;
};
export function ControlQuinquennalCard({
  controleQuinquennal,
  entreprise,
}: ControleQuinquennalCardProps) {
  return (
    <Card
      className={classNames(styles.controleQuinquennal, {
        [styles.archived]:
          controleQuinquennal.status ===
          GatewayControleQuinquennal.ControleQuinquennalStatus.TERMINE,
      })}
    >
      <p className={styles.millesime}>
        {controleQuinquennal.status ===
          GatewayControleQuinquennal.ControleQuinquennalStatus.TERMINE && (
          <IconStatus className={styles.millesimeStatus} isValid />
        )}
        Année {controleQuinquennal.millesime}
      </p>
      <p className={styles.entreprise}>
        {controleQuinquennal.entrepriseName || entreprise?.raisonSociale}
      </p>
      {controleQuinquennal.status ===
        GatewayControleQuinquennal.ControleQuinquennalStatus.EN_COURS && (
        <ComplianceProgressBar
          progress={Math.round(controleQuinquennal.completion)}
        />
      )}
      {controleQuinquennal.status ===
        GatewayControleQuinquennal.ControleQuinquennalStatus.DEPOSE &&
        controleQuinquennal.dateDepot && (
          <TagWithIcon variant="success" className={styles.tag}>
            {`Dossier déposé le ${format(
              controleQuinquennal.dateDepot,
              "dd/MM/yyyy"
            )}`}
          </TagWithIcon>
        )}
      <div className={styles.actionContainer}>
        <ControleQuinquennalCardAction
          controleQuinquennal={controleQuinquennal}
        />
      </div>
    </Card>
  );
}
